import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Quote } from "lucide-react";
import Stars from "@/components/site/Stars";
import ReviewForm from "@/components/site/ReviewForm";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import type { Review } from "@/types";

interface Data { items: Review[]; summary: { avg: number; count: number } }

export function ReviewCard({ r, dark = false }: { r: Review; dark?: boolean }) {
  const { t } = useLang();
  return (
    <article data-testid={`review-${r.id}`} className={`flex flex-col rounded-xl p-6 sm:p-7 ${dark ? "bg-white/5 border border-white/10 text-white" : "bg-white border border-slate-200/80"}`}>
      <Quote className={`h-6 w-6 ${dark ? "text-[#00D2C8]" : "text-[#D4AF37]"}`} />
      <Stars value={r.rating} testId={`review-stars-${r.id}`} />
      <p className={`mt-4 flex-1 text-sm sm:text-base leading-relaxed ${dark ? "text-white/80" : "text-slate-700"}`}>{r.comment}</p>
      <div className="mt-6 flex items-end justify-between gap-3">
        <div>
          <p className={`font-semibold ${dark ? "text-white" : "text-[#0B192C]"}`}>{r.name}</p>
          <p className={`text-xs ${dark ? "text-white/50" : "text-slate-500"}`}>{[r.country, r.yacht_name].filter(Boolean).join(" · ")}</p>
        </div>
        {r.verified && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#008C86]" data-testid={`review-verified-${r.id}`}><BadgeCheck className="h-3.5 w-3.5" />{t("rev.verified")}</span>}
      </div>
    </article>
  );
}

export function HomeReviews() {
  const { t } = useLang();
  const { data } = useQuery<Data>({ queryKey: ["reviews", "home"], queryFn: async () => (await api.get("/reviews", { params: { limit: 6 } })).data });
  if (!data || data.items.length === 0) return null;
  return (
    <section className="bg-[#0B192C] text-white relative grain" data-testid="home-reviews">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="eyebrow !text-[#D4AF37]">{t("rev.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl">{t("rev.title")}</h2>
            <p className="mt-4 text-base text-white/65">{t("rev.sub")}</p>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2">
            <div className="flex items-center gap-3"><span className="font-display text-5xl text-[#F3E5AB]" data-testid="reviews-avg">{data.summary.avg.toFixed(1)}</span><Stars value={data.summary.avg} size="h-5 w-5" testId="reviews-avg-stars" /></div>
            <p className="text-xs text-white/60">{t("rev.avg", { avg: data.summary.avg.toFixed(1), n: data.summary.count })}</p>
          </div>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.items.slice(0, 6).map((r) => <ReviewCard key={r.id} r={r} dark />)}
        </div>
        <div className="mt-12"><span className="[&>button]:border-white/40 [&>button]:text-white [&>button:hover]:bg-white [&>button:hover]:text-[#0B192C]"><ReviewForm /></span></div>
      </div>
    </section>
  );
}

export function YachtReviews({ yachtId }: { yachtId: string }) {
  const { t } = useLang();
  const { data } = useQuery<Data>({ queryKey: ["reviews", yachtId], queryFn: async () => (await api.get("/reviews", { params: { yacht_id: yachtId } })).data });
  const items = data?.items || [];
  return (
    <section data-testid="yacht-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-[#0B192C]">{t("rev.yachtTitle")}</h2>
          {data && data.summary.count > 0 && (
            <div className="mt-3 flex items-center gap-3">
              <Stars value={data.summary.avg} size="h-5 w-5" testId="yacht-reviews-stars" />
              <span className="text-sm text-slate-600" data-testid="yacht-reviews-summary">{t("rev.avg", { avg: data.summary.avg.toFixed(1), n: data.summary.count })}</span>
            </div>
          )}
        </div>
        <ReviewForm yachtId={yachtId} />
      </div>
      {items.length === 0 ? <p className="mt-6 text-sm text-slate-500" data-testid="yacht-reviews-empty">{t("rev.none")}</p> : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2">{items.map((r) => <ReviewCard key={r.id} r={r} />)}</div>
      )}
    </section>
  );
}
